import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreakpoints } from 'hooks/useWindowSize';
import { Modal } from 'shared/components/Modal/Modal';
import { perSessionModalKey, perSessionTermAgreedKey } from 'constants/localStorageKeys';
import { AchModalExitPoint, trackAchModalClosed } from 'modules/status/utils/trackAchModalEvent';
import { AutopayModalContent } from './AutopayModal.styles';
import { AutopayTermConditionModal } from './AutopayTermConditionModal';
import { AutopaySetupModal } from './AutopaySetupModal';
import { AgreementType } from '../../../../api/types';
import { getPrimaryLanguage } from '../../../../shared/utils/getPrimaryLanguage';
import { AutopayCustomBankAccountModal } from './AutopayCustomBankAccountModal';
import { AutopaySuccessModal } from './AutopaySuccessModal';
import { MilestoneType } from '../../../types';

export enum AutoPayStep {
  SETUP_ACH = 1,
  TERM_CONDITION = 2,
  ADD_BANK_ACCOUNT = 3,
  ALREADY_ENROLLED = 4,
}

export type AutopayModalProps = {
  agreement: AgreementType;
  isOpen: boolean;
  modalKey?: string;
  onCloseAutopayModal: () => void;
  onCompleteFormModal: () => void;
  initialStep?: AutoPayStep;
  showEntryPointInSystemDetails: boolean;
};

export function AutopayModal({
  agreement,
  isOpen,
  modalKey,
  onCloseAutopayModal,
  onCompleteFormModal,
  initialStep,
  showEntryPointInSystemDetails,
}: AutopayModalProps) {
  const { t } = useTranslation();
  const screenSize = useBreakpoints();
  const [step, setStep] = useState<AutoPayStep>(initialStep || AutoPayStep.SETUP_ACH);

  useEffect(() => {
    const perSessionModalsRaw = localStorage.getItem(perSessionModalKey);
    if (!showEntryPointInSystemDetails && perSessionModalsRaw !== null) {
      const modalsObj = JSON.parse(perSessionModalsRaw);
      // if this modal has a defined item we've already shown it and can preemptively close the modal

      if (!modalKey || (modalKey && modalsObj[modalKey])) {
        onCloseAutopayModal();
      } else {
        localStorage.setItem(perSessionModalKey, JSON.stringify({ [modalKey]: true }));
      }
    }
  }, [onCloseAutopayModal, modalKey, showEntryPointInSystemDetails]);

  const handleCloseAutopayModal = useCallback(() => {
    if (
      (agreement?.ach_elected && !agreement?.ach_verified) ||
      (agreement?.ach_verified && agreement.current_milestone !== MilestoneType.IN_SERVICE)
    ) {
      setStep(AutoPayStep.ADD_BANK_ACCOUNT);
    } else if (!agreement?.ach_elected) {
      setStep(AutoPayStep.TERM_CONDITION);
    } else {
      setStep(AutoPayStep.SETUP_ACH);
    }

    onCloseAutopayModal();
    trackAchModalClosed(AchModalExitPoint.CLOSE_BUTTON);
  }, [onCloseAutopayModal, agreement]);

  const onAcceptTerm = () => {
    const perSessionTermAgreedRaw = localStorage.getItem(perSessionTermAgreedKey);
    if (perSessionTermAgreedRaw !== null) {
      const termAgreedObj = JSON.parse(perSessionTermAgreedRaw);
      termAgreedObj[agreement.id] = true;
      localStorage.setItem(perSessionTermAgreedKey, JSON.stringify(termAgreedObj));
    } else {
      localStorage.setItem(perSessionTermAgreedKey, JSON.stringify({ [agreement.id]: true }));
    }
  };

  const renderModalContent = () => {
    if (step === AutoPayStep.SETUP_ACH) {
      return (
        <AutopaySetupModal
          onButtonClick={() => {
            let nextStep = AutoPayStep.TERM_CONDITION;
            const perSessionTermAgreedRaw = localStorage.getItem(perSessionTermAgreedKey);

            if (perSessionTermAgreedRaw !== null) {
              const termAgreedObj = JSON.parse(perSessionTermAgreedRaw);
              if (termAgreedObj && termAgreedObj[agreement.id]) {
                nextStep = AutoPayStep.ADD_BANK_ACCOUNT;
              }
            }
            setStep(nextStep);
          }}
        />
      );
    }

    if (step === AutoPayStep.TERM_CONDITION) {
      return (
        <AutopayTermConditionModal
          primaryLanguage={getPrimaryLanguage(agreement.primary_language)}
          onAccept={() => {
            onAcceptTerm();
            setStep(AutoPayStep.ADD_BANK_ACCOUNT);
          }}
          onDecline={() => {
            setStep(AutoPayStep.SETUP_ACH);
          }}
        />
      );
    }

    if (step === AutoPayStep.ALREADY_ENROLLED) {
      return (
        <AutopaySuccessModal
          isAlreadyEnrolled
          onCompleteFormModal={() => {
            trackAchModalClosed(AchModalExitPoint.GOT_IT_BUTTON);
            onCloseAutopayModal();
          }}
        />
      );
    }

    return (
      <AutopayCustomBankAccountModal
        agreementId={agreement.id}
        onCompleteFormModal={onCompleteFormModal}
        onCloseModal={handleCloseAutopayModal}
      />
    );
  };

  return (
    <Modal
      contentLabel={t('autopayModal.neverMissABill')}
      styleVariant="tertiary"
      isOpen={isOpen}
      subHeader
      isFullWidth={screenSize === 'sm'}
      fitContent
      padding="24px"
      onRequestClose={handleCloseAutopayModal}
    >
      <AutopayModalContent>{renderModalContent()}</AutopayModalContent>
    </Modal>
  );
}

export default AutopayModal;
